import { useEffect } from 'react';

interface PublicSiteMetaProps {
  title?: string | null;
  description?: string | null;
  faviconUrl?: string | null;
  socialImageUrl?: string | null;
  siteName?: string | null;
  siteUrl?: string | null;
  alternateName?: string | null;
  isHomePage?: boolean;
  type?: 'website' | 'article';
  publishedTime?: string | null;
}

const MANAGED_META_SELECTOR = 'meta[data-lumenise-site-meta="true"]';
const MANAGED_ICON_SELECTOR = 'link[data-lumenise-site-icon="true"]';
const MANAGED_CANONICAL_SELECTOR = 'link[data-lumenise-site-canonical="true"]';
const MANAGED_JSONLD_SELECTOR = 'script[data-lumenise-site-jsonld="true"]';
const EXISTING_ICON_SELECTOR = 'link[rel~="icon"], link[rel="shortcut icon"], link[rel="apple-touch-icon"], link[rel="mask-icon"]';
const ORIGINAL_HREF_ATTR = 'data-lumenise-original-href';
const ORIGINAL_TYPE_ATTR = 'data-lumenise-original-type';
const ORIGINAL_META_CONTENT_ATTR = 'data-lumenise-original-content';
let activeMetaRun = 0;

function absoluteUrl(value: string | null | undefined): string {
  if (!value) return '';
  try {
    return new URL(value, window.location.origin).toString();
  } catch {
    return value;
  }
}

function setMeta(property: 'property' | 'name', key: string, content: string) {
  const selector = `meta[${property}="${key}"]`;
  const existing = document.head.querySelector<HTMLMetaElement>(selector);
  if (existing) {
    if (!existing.hasAttribute(ORIGINAL_META_CONTENT_ATTR)) {
      existing.setAttribute(ORIGINAL_META_CONTENT_ATTR, existing.getAttribute('content') ?? '');
    }
    existing.setAttribute('content', content);
    existing.setAttribute('data-lumenise-site-meta', 'true');
    return;
  }

  const meta = document.createElement('meta');
  meta.setAttribute(property, key);
  meta.setAttribute('content', content);
  meta.setAttribute('data-lumenise-site-meta', 'true');
  document.head.appendChild(meta);
}

function clearManagedMeta() {
  document.head.querySelectorAll<HTMLMetaElement>(MANAGED_META_SELECTOR).forEach((meta) => {
    if (meta.hasAttribute(ORIGINAL_META_CONTENT_ATTR)) {
      meta.setAttribute('content', meta.getAttribute(ORIGINAL_META_CONTENT_ATTR) ?? '');
      meta.removeAttribute(ORIGINAL_META_CONTENT_ATTR);
      meta.removeAttribute('data-lumenise-site-meta');
      return;
    }
    meta.remove();
  });
}

function iconType(url: string) {
  const pathname = url.split('?')[0]?.toLowerCase() ?? '';
  if (pathname.endsWith('.svg')) return 'image/svg+xml';
  if (pathname.endsWith('.png')) return 'image/png';
  if (pathname.endsWith('.jpg') || pathname.endsWith('.jpeg')) return 'image/jpeg';
  if (pathname.endsWith('.ico')) return 'image/x-icon';
  return '';
}

function appendIcon(rel: string, href: string) {
  const icon = document.createElement('link');
  icon.rel = rel;
  icon.href = href;
  const type = iconType(href);
  if (type) icon.type = type;
  icon.setAttribute('data-lumenise-site-icon', 'true');
  document.head.appendChild(icon);
}

function appendCanonical(href: string) {
  const canonical = document.createElement('link');
  canonical.rel = 'canonical';
  canonical.href = href;
  canonical.setAttribute('data-lumenise-site-canonical', 'true');
  document.head.appendChild(canonical);
}

function appendSiteStructuredData(siteName: string, siteUrl: string, alternateName?: string) {
  const script = document.createElement('script');
  script.type = 'application/ld+json';
  script.setAttribute('data-lumenise-site-jsonld', 'true');
  const website: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: siteName,
    url: siteUrl,
  };
  const normalizedAlternateName = alternateName?.trim();
  if (normalizedAlternateName && normalizedAlternateName !== siteName) {
    website.alternateName = normalizedAlternateName;
  }
  script.textContent = JSON.stringify(website).replaceAll('<', '\\u003c');
  document.head.appendChild(script);
}

function currentCanonicalUrl() {
  const url = new URL(window.location.href);
  url.search = '';
  url.hash = '';
  return url.toString();
}

function updateExistingIcons(href: string) {
  const type = iconType(href);
  document.head.querySelectorAll<HTMLLinkElement>(EXISTING_ICON_SELECTOR).forEach((icon) => {
    if (icon.matches(MANAGED_ICON_SELECTOR)) return;

    if (!icon.hasAttribute(ORIGINAL_HREF_ATTR)) {
      icon.setAttribute(ORIGINAL_HREF_ATTR, icon.getAttribute('href') ?? '');
      icon.setAttribute(ORIGINAL_TYPE_ATTR, icon.getAttribute('type') ?? '');
    }

    icon.href = href;
    if (type) icon.type = type;
    else icon.removeAttribute('type');
  });
}

function restoreExistingIcons() {
  document.head.querySelectorAll<HTMLLinkElement>(`link[${ORIGINAL_HREF_ATTR}]`).forEach((icon) => {
    const originalHref = icon.getAttribute(ORIGINAL_HREF_ATTR) ?? '';
    const originalType = icon.getAttribute(ORIGINAL_TYPE_ATTR) ?? '';

    if (originalHref) icon.setAttribute('href', originalHref);
    else icon.removeAttribute('href');

    if (originalType) icon.setAttribute('type', originalType);
    else icon.removeAttribute('type');

    icon.removeAttribute(ORIGINAL_HREF_ATTR);
    icon.removeAttribute(ORIGINAL_TYPE_ATTR);
  });
}

export function PublicSiteMeta({
  title,
  description,
  faviconUrl,
  socialImageUrl,
  siteName,
  siteUrl,
  alternateName,
  isHomePage = false,
  type = 'website',
  publishedTime,
}: PublicSiteMetaProps) {
  useEffect(() => {
    const runId = activeMetaRun + 1;
    activeMetaRun = runId;
    const previousTitle = document.title;
    const nextTitle = (title ?? '').trim();
    if (nextTitle) document.title = nextTitle;

    clearManagedMeta();
    document.head.querySelectorAll(MANAGED_ICON_SELECTOR).forEach((node) => node.remove());
    document.head.querySelectorAll(MANAGED_CANONICAL_SELECTOR).forEach((node) => node.remove());
    document.head.querySelectorAll(MANAGED_JSONLD_SELECTOR).forEach((node) => node.remove());

    const imageUrl = absoluteUrl(socialImageUrl);
    const iconUrl = absoluteUrl(faviconUrl);
    const metaDescription = (description ?? '').trim();
    const canonicalUrl = currentCanonicalUrl();
    const normalizedSiteName = (siteName ?? '').trim();
    const normalizedSiteUrl = absoluteUrl(siteUrl) || new URL('/', window.location.origin).toString();

    appendCanonical(canonicalUrl);
    setMeta('property', 'og:url', canonicalUrl);
    setMeta('property', 'og:type', type);

    if (nextTitle) {
      setMeta('property', 'og:title', nextTitle);
      setMeta('name', 'twitter:title', nextTitle);
    }
    if (normalizedSiteName) setMeta('property', 'og:site_name', normalizedSiteName);
    if (isHomePage && normalizedSiteName) appendSiteStructuredData(normalizedSiteName, normalizedSiteUrl, alternateName ?? undefined);

    if (metaDescription) {
      setMeta('name', 'description', metaDescription);
      setMeta('property', 'og:description', metaDescription);
      setMeta('name', 'twitter:description', metaDescription);
    }

    if (type === 'article' && publishedTime) {
      setMeta('property', 'article:published_time', publishedTime);
    }

    if (imageUrl) {
      setMeta('property', 'og:image', imageUrl);
      setMeta('name', 'twitter:image', imageUrl);
      setMeta('name', 'twitter:card', 'summary_large_image');
    }

    if (iconUrl) {
      updateExistingIcons(iconUrl);
      appendIcon('icon', iconUrl);
      appendIcon('shortcut icon', iconUrl);
      appendIcon('apple-touch-icon', iconUrl);
    }

    return () => {
      if (activeMetaRun !== runId) return;
      document.title = previousTitle;
      clearManagedMeta();
      document.head.querySelectorAll(MANAGED_ICON_SELECTOR).forEach((node) => node.remove());
      document.head.querySelectorAll(MANAGED_CANONICAL_SELECTOR).forEach((node) => node.remove());
      document.head.querySelectorAll(MANAGED_JSONLD_SELECTOR).forEach((node) => node.remove());
      restoreExistingIcons();
    };
  }, [title, description, faviconUrl, socialImageUrl, siteName, siteUrl, alternateName, isHomePage, type, publishedTime]);

  return null;
}
